import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import Select, { SelectOption } from '@/components/select/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/Table';
import { NotificationRuleDto } from '@/services/api/contracts/adminNotifications';
import { 
  useCreateNotificationRule, 
  useGetNotificationRules, 
  useUpdateNotificationRule, 
  useDeleteNotificationRule,
  useGetNotificationEvents
} from '@/services/api/hooks/adminNotifications';
import { usePartners } from '@/services/api/hooks/partners';
import { useCompanies } from '@/services/api/hooks/companies';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Pencil, Trash } from 'lucide-react';

type ScopeType = 'Global' | 'MSP' | 'Customer';

const RuleManager: FC = () => {
  const [rules] = useGetNotificationRules();
  const [events] = useGetNotificationEvents();
  const { mutateAsync: createRule } = useCreateNotificationRule();
  const { mutateAsync: updateRule } = useUpdateNotificationRule();
  const { mutateAsync: deleteRule } = useDeleteNotificationRule();

  const [msps] = usePartners();
  
  const [selectedScope, setSelectedScope] = useState<ScopeType>('Global');
  const [selectedMspId, setSelectedMspId] = useState<string | null>(null);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  // Fetch companies only if an MSP is selected and scope is Customer
  const [companies] = useCompanies(selectedMspId || '');

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<NotificationRuleDto>({
    resolver: zodResolver(NotificationRuleDto),
    defaultValues: {
      targetType: 'SpecificUser',
      isActive: true
    }
  });

  const currentTargetType = useWatch({ control, name: 'targetType' });

  let showTargetId = true;
  let targetIdLabel = 'Target ID (Destination)';
  if (currentTargetType === 'SpecificUser') targetIdLabel = 'Email Address';
  else if (currentTargetType === 'Webhook') targetIdLabel = 'Webhook URL';
  else if (currentTargetType === 'Role') targetIdLabel = 'Role Name / ID';
  else {
    showTargetId = false;
  }

  useEffect(() => {
    if (!showTargetId) {
      setValue('targetId', '');
    }
  }, [showTargetId, setValue]);

  const onSubmit = async (data: NotificationRuleDto) => {
    // Override scope based on UI selection to prevent accidental scopes
    const payload: NotificationRuleDto = {
      ...data,
      scopeMspId: selectedScope === 'MSP' || selectedScope === 'Customer' ? data.scopeMspId : null,
      scopeCompanyId: selectedScope === 'Customer' ? data.scopeCompanyId : null
    };

    if (editingRuleId) {
      await updateRule({ id: editingRuleId, rule: payload });
    } else {
      await createRule(payload);
    }
    
    cancelEdit();
  };

  const handleEdit = (rule: NotificationRuleDto) => {
    setEditingRuleId(rule.id!);
    
    // Set scope state based on rule data
    if (rule.scopeCompanyId) {
      setSelectedScope('Customer');
      setSelectedMspId(rule.scopeMspId || null);
    } else if (rule.scopeMspId) {
      setSelectedScope('MSP');
      setSelectedMspId(rule.scopeMspId);
    } else {
      setSelectedScope('Global');
      setSelectedMspId(null);
    }
    
    reset(rule);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
      await deleteRule(id);
    }
  };

  const cancelEdit = () => {
    setEditingRuleId(null);
    setSelectedScope('Global');
    setSelectedMspId(null);
    reset({
      notificationEventId: '',
      targetType: 'SpecificUser',
      targetId: '',
      scopeCompanyId: null,
      scopeMspId: null,
      isActive: true
    });
  };

  const scopeOptions: SelectOption[] = [
    { label: 'Global (Fallback)', value: 'Global' },
    { label: 'Specific MSP', value: 'MSP' },
    { label: 'Specific Customer', value: 'Customer' }
  ];

  const targetTypeOptions: SelectOption[] = [
    { label: 'Specific User (Email)', value: 'SpecificUser' },
    { label: 'Webhook', value: 'Webhook' },
    { label: 'Role', value: 'Role' },
    { label: 'Company', value: 'Company' },
    { label: 'MSP', value: 'Msp' },
    { label: 'Global', value: 'Global' }
  ];

  const mspOptions: SelectOption[] = msps?.map((m: any) => ({ label: m.name, value: m.id })) || [];
  const companyOptions: SelectOption[] = companies?.map((c: any) => ({ label: c.name, value: c.id })) || [];
  const eventSelectOptions: SelectOption[] = events?.map((e: any) => ({ label: e.name, value: e.id })) || [];

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h4 className="text-xl font-bold mb-4">{editingRuleId ? 'Edit Rule' : 'Create New Rule'}</h4>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notification Event</label>
            <Controller
              name="notificationEventId"
              control={control}
              render={({ field }) => (
                <Select
                  options={eventSelectOptions}
                  selectedValue={eventSelectOptions.find(o => o.value === field.value)}
                  onChange={(val) => {
                    const option = val as SelectOption;
                    if (option) field.onChange(option.value);
                  }}
                />
              )}
            />
            {errors.notificationEventId && <p className="mt-1 text-sm text-red-600">{errors.notificationEventId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Type</label>
            <Controller
              name="targetType"
              control={control}
              render={({ field }) => (
                <Select
                  options={targetTypeOptions}
                  selectedValue={targetTypeOptions.find(o => o.value === field.value) || { label: field.value as string, value: field.value as string }}
                  onChange={(val) => {
                    const option = val as SelectOption;
                    if (option) field.onChange(option.value);
                  }}
                />
              )}
            />
            {errors.targetType && <p className="mt-1 text-sm text-red-600">{errors.targetType.message}</p>}
          </div>

          {showTargetId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">{targetIdLabel}</label>
              <input
                type="text"
                {...register('targetId')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder={`e.g. ${currentTargetType === 'Webhook' ? 'https://...' : 'admin@example.com'}`}
              />
              {errors.targetId && (
                <p className="mt-1 text-sm text-red-600">{errors.targetId.message}</p>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-gray-100">
            <h5 className="font-semibold mb-3">Scoping</h5>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope Level</label>
                <Select
                  options={scopeOptions}
                  selectedValue={scopeOptions.find(o => o.value === selectedScope)}
                  onChange={(val) => {
                    const option = val as SelectOption;
                    if (option) setSelectedScope(option.value as ScopeType);
                  }}
                />
              </div>

              {(selectedScope === 'MSP' || selectedScope === 'Customer') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select MSP</label>
                  <Controller
                    name="scopeMspId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={mspOptions}
                        selectedValue={mspOptions.find(o => o.value === field.value)}
                        onChange={(val) => {
                          const option = val as SelectOption;
                          if (option) {
                            field.onChange(option.value);
                            setSelectedMspId(option.value);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              )}

              {selectedScope === 'Customer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
                  <Controller
                    name="scopeCompanyId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={companyOptions}
                        selectedValue={companyOptions.find(o => o.value === field.value)}
                        onChange={(val) => {
                          const option = val as SelectOption;
                          if (option) field.onChange(option.value);
                        }}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingRuleId ? 'Update Rule' : 'Create Rule')}
            </Button>
            {editingRuleId && (
              <Button type="button" variant={ButtonVariant.Secondary} onClick={cancelEdit}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h4 className="text-xl font-bold mb-4">Existing Rules</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Target Type</TableHead>
              <TableHead>Target ID</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-gray-500 py-4">
                  No rules found.
                </TableCell>
              </TableRow>
            )}
            {rules?.map((rule: NotificationRuleDto, idx: number) => {
              let scopeStr = 'Global';
              if (rule.scopeCompanyId) scopeStr = 'Customer';
              else if (rule.scopeMspId) scopeStr = 'MSP';

              const evt = events?.find(e => e.id === rule.notificationEventId);
              const eventName = evt ? evt.name : `Unknown Event (${rule.notificationEventId})`;

              return (
                <TableRow key={rule.id || idx}>
                  <TableCell className="font-mono text-xs">{eventName}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {scopeStr}
                    </span>
                  </TableCell>
                  <TableCell>{rule.targetType}</TableCell>
                  <TableCell>{rule.targetId}</TableCell>
                  <TableCell>{rule.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => handleEdit(rule)}
                      className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                      title="Edit"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(rule.id!)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1 ml-2"
                      title="Delete"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RuleManager;
